import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * UsageLimitsContext - Manages usage limits and warnings (SAT-110)
 *
 * Features:
 * - Configurable daily/monthly token limits
 * - Spending cap option
 * - Soft limit (warning) vs hard limit (block)
 * - Warning thresholds at 80% and 95%
 * - Persisted to localStorage
 */

const STORAGE_KEY = 'decisiv-performance-usage-limits';

const DEFAULT_LIMITS = {
  // Daily limits
  dailyTokenLimit: 100000,
  dailyTokenLimitEnabled: false,
  dailyCostLimit: 5.00,
  dailyCostLimitEnabled: false,
  // Monthly limits
  monthlyTokenLimit: 2000000,
  monthlyTokenLimitEnabled: false,
  monthlyCostLimit: 50.00,
  monthlyCostLimitEnabled: false,
  // Behavior settings
  hardLimitEnabled: false, // If true, blocks requests when limit exceeded
  warningThreshold: 80, // Show warning at this percentage
  criticalThreshold: 95, // Show critical warning at this percentage
};

const UsageLimitsContext = createContext(null);

export function UsageLimitsProvider({ children }) {
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [currentUsage, setCurrentUsage] = useState({
    dailyTokens: 0,
    dailyCost: 0,
    monthlyTokens: 0,
    monthlyCost: 0,
    lastReset: {
      daily: new Date().toDateString(),
      monthly: `${new Date().getFullYear()}-${new Date().getMonth()}`,
    },
  });
  const [warnings, setWarnings] = useState([]);

  // Load limits from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setLimits({ ...DEFAULT_LIMITS, ...parsed.limits });
        setCurrentUsage(prev => ({ ...prev, ...parsed.usage }));
      }
    } catch (e) {
      console.error('Error loading usage limits:', e);
    }
  }, []);

  // Save to localStorage when limits or usage change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ limits, usage: currentUsage }));
    } catch (e) {
      console.error('Error saving usage limits:', e);
    }
  }, [limits, currentUsage]);

  // Check for daily/monthly reset
  useEffect(() => {
    const today = new Date().toDateString();
    const currentMonth = `${new Date().getFullYear()}-${new Date().getMonth()}`;

    setCurrentUsage(prev => {
      const updated = { ...prev };

      // Reset daily if it's a new day
      if (prev.lastReset.daily !== today) {
        updated.dailyTokens = 0;
        updated.dailyCost = 0;
        updated.lastReset = { ...prev.lastReset, daily: today };
      }

      // Reset monthly if it's a new month
      if (prev.lastReset.monthly !== currentMonth) {
        updated.monthlyTokens = 0;
        updated.monthlyCost = 0;
        updated.lastReset = { ...prev.lastReset, monthly: currentMonth };
      }

      return updated;
    });
  }, []);

  // Update limits
  const updateLimits = useCallback((newLimits) => {
    setLimits(prev => ({ ...prev, ...newLimits }));
  }, []);

  // Calculate usage percentages
  const getUsagePercentages = useCallback(() => {
    return {
      dailyTokens: limits.dailyTokenLimitEnabled
        ? Math.min((currentUsage.dailyTokens / limits.dailyTokenLimit) * 100, 100)
        : 0,
      dailyCost: limits.dailyCostLimitEnabled
        ? Math.min((currentUsage.dailyCost / limits.dailyCostLimit) * 100, 100)
        : 0,
      monthlyTokens: limits.monthlyTokenLimitEnabled
        ? Math.min((currentUsage.monthlyTokens / limits.monthlyTokenLimit) * 100, 100)
        : 0,
      monthlyCost: limits.monthlyCostLimitEnabled
        ? Math.min((currentUsage.monthlyCost / limits.monthlyCostLimit) * 100, 100)
        : 0,
    };
  }, [limits, currentUsage]);

  // Check if any limit is exceeded
  const checkLimits = useCallback(() => {
    const percentages = getUsagePercentages();
    const newWarnings = [];

    // Check each enabled limit
    const checks = [
      { key: 'dailyTokens', label: 'Daily token limit', enabled: limits.dailyTokenLimitEnabled },
      { key: 'dailyCost', label: 'Daily spending limit', enabled: limits.dailyCostLimitEnabled },
      { key: 'monthlyTokens', label: 'Monthly token limit', enabled: limits.monthlyTokenLimitEnabled },
      { key: 'monthlyCost', label: 'Monthly spending limit', enabled: limits.monthlyCostLimitEnabled },
    ];

    checks.forEach(({ key, label, enabled }) => {
      if (!enabled) return;
      const percentage = percentages[key];

      if (percentage >= 100) {
        newWarnings.push({
          id: `${key}-exceeded`,
          type: 'exceeded',
          message: `${label} exceeded! ${limits.hardLimitEnabled ? 'Further requests are blocked.' : 'Consider reducing usage.'}`,
          percentage,
          key,
        });
      } else if (percentage >= limits.criticalThreshold) {
        newWarnings.push({
          id: `${key}-critical`,
          type: 'critical',
          message: `${label} at ${percentage.toFixed(0)}%! Approaching limit.`,
          percentage,
          key,
        });
      } else if (percentage >= limits.warningThreshold) {
        newWarnings.push({
          id: `${key}-warning`,
          type: 'warning',
          message: `${label} at ${percentage.toFixed(0)}%. Consider monitoring usage.`,
          percentage,
          key,
        });
      }
    });

    setWarnings(newWarnings);
    return newWarnings;
  }, [limits, getUsagePercentages]);

  // Check if request should be blocked (hard limit)
  const isBlocked = useCallback(() => {
    if (!limits.hardLimitEnabled) return false;

    const percentages = getUsagePercentages();
    return (
      (limits.dailyTokenLimitEnabled && percentages.dailyTokens >= 100) ||
      (limits.dailyCostLimitEnabled && percentages.dailyCost >= 100) ||
      (limits.monthlyTokenLimitEnabled && percentages.monthlyTokens >= 100) ||
      (limits.monthlyCostLimitEnabled && percentages.monthlyCost >= 100)
    );
  }, [limits, getUsagePercentages]);

  // Record usage (called after each API request)
  const recordUsage = useCallback((tokens, cost) => {
    setCurrentUsage(prev => ({
      ...prev,
      dailyTokens: prev.dailyTokens + tokens,
      dailyCost: prev.dailyCost + cost,
      monthlyTokens: prev.monthlyTokens + tokens,
      monthlyCost: prev.monthlyCost + cost,
    }));
    // Check limits after recording
    setTimeout(checkLimits, 100);
  }, [checkLimits]);

  // Get reset timing info
  const getResetInfo = useCallback(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    const dailyResetIn = tomorrow - now;
    const monthlyResetIn = nextMonth - now;

    const formatDuration = (ms) => {
      const hours = Math.floor(ms / (1000 * 60 * 60));
      const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    };

    return {
      dailyResetIn: formatDuration(dailyResetIn),
      monthlyResetIn: formatDuration(monthlyResetIn),
      dailyResetDate: tomorrow.toLocaleDateString(),
      monthlyResetDate: nextMonth.toLocaleDateString(),
    };
  }, []);

  // Dismiss a warning
  const dismissWarning = useCallback((warningId) => {
    setWarnings(prev => prev.filter(w => w.id !== warningId));
  }, []);

  // No-op: usage fetching is admin-only and should only happen on the UsagePage.
  // Keeping this function stub so callers don't break.
  const fetchUsageFromAPI = useCallback(async () => {}, []);

  const value = {
    limits,
    currentUsage,
    warnings,
    updateLimits,
    recordUsage,
    getUsagePercentages,
    checkLimits,
    isBlocked,
    getResetInfo,
    dismissWarning,
    fetchUsageFromAPI,
  };

  return (
    <UsageLimitsContext.Provider value={value}>
      {children}
    </UsageLimitsContext.Provider>
  );
}

export function useUsageLimits() {
  const context = useContext(UsageLimitsContext);
  if (!context) {
    throw new Error('useUsageLimits must be used within a UsageLimitsProvider');
  }
  return context;
}

export default UsageLimitsContext;
